import { useState, useEffect } from 'react';
import { blogApiClient, type ApiPostSummary } from '@/lib/blogApiClient';
import type { BlogPost } from '@/types/blog';

/** Shape returned by hooks — matches BlogPost but content is always undefined in list views. */
export type PostSummary = BlogPost;

function fromApi(p: ApiPostSummary): PostSummary {
  return {
    slug:        p.slug,
    title:       p.title,
    description: p.description,
    date:        p.date,
    author:      p.author,
    category:    p.category,
    tags:        p.tags,
    featured:    p.featured,
    status:      p.status,
    readingTime: p.readingTime,
  };
}

interface PostsState {
  posts: PostSummary[];
  isLoading: boolean;
  error: string | null;
}

/** All published posts, sorted newest first. */
export function usePosts(): PostsState {
  const [state, setState] = useState<PostsState>({ posts: [], isLoading: true, error: null });

  useEffect(() => {
    blogApiClient.listPosts()
      .then(({ posts }) => setState({ posts: posts.map(fromApi), isLoading: false, error: null }))
      .catch((err: unknown) => setState({ posts: [], isLoading: false, error: String(err) }));
  }, []);

  return state;
}

/** Featured posts, falling back to the first two if none are marked featured. */
export function useFeaturedPosts(): PostsState {
  const { posts, isLoading, error } = usePosts();
  const featured = posts.filter((p) => p.featured);
  return {
    posts: featured.length ? featured : posts.slice(0, 2),
    isLoading,
    error,
  };
}

/** Unique categories derived from published posts. */
export function useCategories(): { categories: string[]; isLoading: boolean } {
  const { posts, isLoading } = usePosts();
  const categories = [...new Set(posts.map((p) => p.category))];
  return { categories, isLoading };
}

/** All unique tags, sorted alphabetically. */
export function useTags(): { tags: string[]; isLoading: boolean } {
  const { posts, isLoading } = usePosts();
  const tags = [...new Set(posts.flatMap((p) => p.tags))].sort();
  return { tags, isLoading };
}

/** Posts filtered by category (case-insensitive). */
export function usePostsByCategory(category: string): PostsState {
  const { posts, isLoading, error } = usePosts();
  return {
    posts: posts.filter((p) => p.category.toLowerCase() === category.toLowerCase()),
    isLoading,
    error,
  };
}

/** Posts filtered by tag (case-insensitive). */
export function usePostsByTag(tag: string): PostsState {
  const { posts, isLoading, error } = usePosts();
  return {
    posts: posts.filter((p) => p.tags.some((t) => t.toLowerCase() === tag.toLowerCase())),
    isLoading,
    error,
  };
}
